import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import {
    BookingActions,
    BookingSelectors,
    BookingsSelectors,
    Choice,
    ChoiceItem,
    DeepLinksActions,
    HandlerErrorService,
    LocalizationSelectors,
    ModalOverlayRef,
    ModulesSelectors,
    TrackingActions,
} from '@serviceos-ng/core';
import { Observable, Subscription } from 'rxjs';
import { concatMap } from 'rxjs/operators';

@Component({
    selector: 'serviceos-web-rate-booking-modal',
    templateUrl: './rate-booking-modal.component.html',
    styleUrls: ['./rate-booking-modal.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class RateBookingModalComponent implements OnInit, OnDestroy {
    @Select(ModulesSelectors.getComponentFromModule('bookings', 'rate_modal'))
    rateModalConfig$!: Observable<any>;
    rateModalConfig: any = null;

    componentConfigSubscription: Subscription | null = null;

    public form: { id: number; choices: Choice[] } = {
        id: 151,
        choices: [],
    };

    public completeScreen: any | null;

    // rate choice_item whe create the choice_item hare so we can save the reference between the 2 places where is use.
    public choiceItemRate: ChoiceItem = {
        id: 12111,
        title:
            'Your experience and feedback are what makes Fantastic Services truly fantastic. So, how would you rate it?',
        required: true,
        description: '',
        customize: {},
        sort: 11,
        type: 'rate',
        value: 0,
        choice_items: [],
    };

    public showFormLoader = false;
    public formErrorMessage: any = '';
    public lastRateValue = 0;
    public tnxPage = false;
    public showTip = false;
    public tipProCondition = false;

    public rateShortcodes = {
        'rate.unit_name': '',
        'rate.rating_value': 0,
    };

    public texts = {
        rating_stars_choice_title:
            "We'd love to hear about your experience with us!",
        rating_stars_choice_on_submit: 'Send feedback',
        rating_tags_choice_title_positive:
            "We're so happy that you've had a fantastic experience!",
        rating_tags_choice_title_negative:
            'We’re sorry for the not-so-fantastic experience.',
        rating_tags_choice_on_submit: 'Submit',
        comment_choice_item_cta_text: 'Add comment',
        stars_choice_item_title:
            'Your experience and feedback are what makes Fantastic Services truly fantastic. So, how would you rate it?',
        stars_choice_item_title_negative:
            'Please, share what went wrong and how we can improve.',
        stars_choice_item_title_positive: 'Please share what you liked:',
        unit_text: 'How was your service with [$rate.unit_name]?',
    };

    public booking: any = null;

    constructor(
        public modalRef: ModalOverlayRef,
        private store: Store,
        private translateService: TranslateService,
        private translateParser: TranslateParser,
        private handlerErrorService: HandlerErrorService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        const shortcodes = this.store.selectSnapshot(
            LocalizationSelectors.getShortcodes()
        );

        this.componentConfigSubscription = this.rateModalConfig$.subscribe(
            (rateModalConfig) => {
                if (rateModalConfig) {
                    this.texts = rateModalConfig;
                    this.rateModalConfig = rateModalConfig;
                }
            }
        );

        // get rated booking object.
        const bookingId = this.modalRef.configData.deep_link_object
            .fs_element_id;
        this.booking = this.store.selectSnapshot(BookingSelectors.getSingle);

        if (
            !(
                this.booking?.id == bookingId ||
                this.booking?.objectId == bookingId
            )
        ) {
            this.booking = this.store.selectSnapshot(
                BookingsSelectors.getById(
                    this.modalRef.configData.deep_link_object.fs_element_id
                )
            );
        }

        this.buildFormChoices();

        const tipSectionConfig = this.store.selectSnapshot(ModulesSelectors.getComponentFromModule('tip', 'tip_section'));

        this.tipProCondition = tipSectionConfig && this.booking && this.booking.tip_choice ? true : false;
    }

    ngOnDestroy(): void {
        this.componentConfigSubscription?.unsubscribe();
    }

    /**
     * Mapping method for edit booking reasons with our choice and choice item structure
     */
    private buildFormChoices(): void {

        this.choiceItemRate.title = this.texts.stars_choice_item_title;

        if (this.booking && (this.booking.unit || this.booking.team)) {
            const unit = this.booking.unit || this.booking.team;
            this.rateShortcodes['rate.unit_name'] = unit.name;

            this.choiceItemRate.customize.unit_img = unit.image_url;
            this.choiceItemRate.customize.unit_text = this.translateParser.interpolate(
                this.texts.unit_text,
                this.rateShortcodes
            );
        }

        // build main choice
        // eslint-disable-next-line prefer-const
        const firstScreen: Choice = {
            id: 1211,
            title: this.texts.rating_stars_choice_title,
            description: '',
            sort: 1,
            positions: ['none'],
            type: 'default',
            required: true,
            submit: 'on_submit',
            summary_title: '',
            customize: {
                actions_button_styles: {
                    on_submit: 'primary',
                },
            },
            actions: [
                {
                    endpoint: null,
                    type: 'on_submit',
                    active_to: this.modalRef.configData.active_to,
                    deep_link: 'action=update_rate_condition',
                    title: this.texts.rating_stars_choice_on_submit,
                },
            ],
            choice_items: [this.choiceItemRate],
        };

        const secondScreen: Choice = {
            id: 1212,
            title: this.texts.rating_tags_choice_title_positive,
            description: '',
            sort: 1,
            positions: ['none'],
            type: 'default',
            required: true,
            submit: 'on_change',
            summary_title: '',
            customize: {
                actions_button_styles: {
                    on_submit: 'primary',
                },
            },
            actions: [
                {
                    endpoint: null,
                    type: 'on_change',
                    active_to: this.modalRef.configData.active_to,
                    deep_link: 'action=update_rate_condition',
                    title: '',
                },
                {
                    endpoint: null,
                    type: 'on_submit',
                    active_to: this.modalRef.configData.active_to,
                    deep_link: 'action=submit_rate',
                    title: this.texts.rating_tags_choice_on_submit,
                },
            ],
            choice_items: [],
        };

        this.form.choices.push(firstScreen, secondScreen);
    }

    /**
     * Action handler for form
     * @param action
     */
    public onFormAction(action: any): void {
        const bookingId = this.modalRef?.configData?.deep_link_object?.fs_element_id,
            bookingScreen = this.modalRef?.configData?.deep_link_object?.fs_screen,
            rateOpenerType: string = this.modalRef.configData.type;

        if (action.deep_link == 'action=update_rate_condition') {
            if (action.type === 'on_submit') {
                this.conditionRate();

                // Send data layer event
                this.store.dispatch(
                    new TrackingActions.Push(`${rateOpenerType === 'complain' ? 'complaint' : rateOpenerType}_stars_selected`, {
                        stars: this.extractPayloadData().rating,
                        position: this.modalRef?.configData.initiator_position || null,
                        booking_id: bookingId
                    })
                );
            }
        }

        if (action.deep_link == 'action=submit_rate') {
            this.showFormLoader = true;
            this.formErrorMessage = '';

            const payloadData = this.extractPayloadData();

            this.store.dispatch(
                new TrackingActions.Push(`${rateOpenerType === 'complain' ? 'complaint' : rateOpenerType}_submitted`, {
                    stars: payloadData.rating,
                    comment: payloadData.comment,
                    position: this.modalRef?.configData.initiator_position || null,
                    booking_id: bookingId
                })
            );

            this.store
                .dispatch(
                    new DeepLinksActions.SubmitDeepLinkResult$(
                        this.modalRef.configData,
                        payloadData
                    )
                )
                .pipe(
                    concatMap(() =>
                        this.store.dispatch(
                            new BookingActions.Refresh(bookingId, bookingScreen)
                        )
                    )
                )
                .subscribe(
                    (res) => {
                        this.showFormLoader = false;
                        const completeRate = JSON.parse(JSON.stringify(this.rateModalConfig));

                        const screens = {
                            positive_rate_complete: null,
                            negative_rate_complete: null,
                        };

                        completeRate.screens.forEach((el) => {
                            el.items = el.items.map((item) => {
                                item.properties.title = this.translateParser.interpolate(
                                    item.properties.title,
                                    this.rateShortcodes
                                );
                                return item;
                            });

                            screens[el.screen] = el.items;
                        });

                        if (this.lastRateValue >= 4) {
                            this.completeScreen =
                                screens?.positive_rate_complete || null;
                        } else {
                            this.completeScreen =
                                screens.negative_rate_complete || null;
                        }

                        if (!this.completeScreen) {
                            this.modalRef.close();
                        } else {
                            if (this.lastRateValue >= 4 && this.tipProCondition) {
                                // Send data layer event
                                this.store.dispatch(
                                    new TrackingActions.Push('tip_button_viewed', {
                                        position: 'rate',
                                        booking_id: this.booking?.id ? this.booking.id : this.booking?.objectId
                                    })
                                );
                            }

                            this.tnxPage = true;
                        }
                    },
                    (error) => {
                        this.formErrorMessage = error?.error[0]?.message;
                    }
                );
        }
    }

    /**
     * Extract payload data for edit request
     */
    private extractPayloadData() {
        // eslint-disable-next-line prefer-const
        let rateBookingPayloadData: any = {
            rating: 0,
            tags: [],
            comment: '',
        };

        this.form.choices[1].choice_items?.map((choiceItem) => {
            if (choiceItem.id == 12123) {
                rateBookingPayloadData.comment = choiceItem.value;
                return;
            }

            if (choiceItem.id == 12111) {
                rateBookingPayloadData.rating = choiceItem.value;
                return;
            }

            if (choiceItem.value == 1) {
                rateBookingPayloadData.tags.push(choiceItem.id);
            }
        });

        return rateBookingPayloadData;
    }

    private conditionRate() {
        if (this.choiceItemRate.value != this.lastRateValue) {
            this.lastRateValue = this.choiceItemRate.value;
            this.rateShortcodes['rate.rating_value'] = this.lastRateValue;
        } else {
            return;
        }

        const secondScreen = this.form.choices[1];
        const conditions = [this.choiceItemRate];

        // tags
        this.modalRef.configData.resources.tags.forEach((tag) => {
            if (
                tag.applies_for_ratings.indexOf(this.choiceItemRate.value) == -1
            ) {
                return;
            }

            conditions.push({
                id: tag.id,
                title: tag.name,
                description: '',
                required: false,
                customize: {
                    chips: true,
                },
                sort: tag.sort,
                type: 'checkbox',
                value: 0,
                choice_items: [],
            });
        });

        this.choiceItemRate.customize = {};
        if (this.choiceItemRate.value > 3) {
            secondScreen.title = this.texts.rating_tags_choice_title_positive;
            this.choiceItemRate.title = this.texts.stars_choice_item_title_positive;
        } else {
            secondScreen.title = this.texts.rating_tags_choice_title_negative;
            this.choiceItemRate.title = this.texts.stars_choice_item_title_negative;
        }

        conditions.push({
            id: 12123,
            title: '',
            customize: {
                expandable: true,
                expand_cta_text: this.texts.comment_choice_item_cta_text,
            },
            required: true,
            choice_items: [],
            sort: 112,
            type: 'text_field',
            value: '',
        });

        secondScreen.choice_items = conditions;
    }

    onDone() {
        this.modalRef.close();
    }

    public onTipEvent(eventObject: { data: any; event: string; module: string }): void {
        if (eventObject && eventObject.event) {
            if (eventObject.event === 'did_finish_tipping') {
                this.modalRef.close();
            }
            
            if (eventObject.event === 'track' && eventObject.data) {
                const trackingObject: any = eventObject.data,
                    trackingData: {
                        booking_id: string;
                        booking_transaction_id: string;
                        position: string;
                        type?: string;
                        value?: string;
                    } = trackingObject.data;

                this.store.dispatch(
                    new TrackingActions.Push(trackingObject.event, trackingData)
                );
            }
        }
    }

    public showTipPro(): void {
        this.showTip = true;

        // Send data layer event
        this.store.dispatch(
            new TrackingActions.Push('tip_button_tapped', {
                position: 'rate',
                booking_id: this.booking?.id ? this.booking.id : this.booking.objectId
            })
        );
    }
}
